import {
  Component,
  input,
  signal,
  QueryList,
  ViewChildren,
  ViewChild,
  ElementRef,
  output,
  effect,
  computed,
  inject,
  AfterViewInit,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  FilterInterface,
  FilterSet,
  FilterSetType,
} from "src/app/core/interfaces/filter-interface";
import { Icon } from "../utils/icon/icon";
import { PianoAnalyticsService } from "src/app/core/services/piano-analytics";
import { TranslocoModule } from "@jsverse/transloco";
import { Button } from "../utils/button/button";
import { FiltersService } from "src/app/core/services/filters/filters-service";

@Component({
  selector: "app-filter-section",
  imports: [Button, Icon, TranslocoModule],
  templateUrl: "./filter-section.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      button[filterSelected] {
        background-color: var(--color-vinciLavender);
        border-color: var(--color-vinciBlue-600);
      }

      button[categorySelected] {
        background-color: var(--color-vinciLavender);
      }

      .filters-scrollbar {
        scrollbar-color: var(--color-vinciBlue-600) var(--color-vinciGrey-200);
        scrollbar-width: thin;
      }
    `,
  ],
})
export class FilterSection implements AfterViewInit, OnInit {
  onSearchInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.searchBarValue.set(inputElement.value);
    this.updateSearchValue.emit(this.searchBarValue());
    this.isEmpty.emit(
      this.filtersSelected().length === 0 && this.searchBarValue().length === 0
    );
  }

  piano = inject(PianoAnalyticsService);
  filtersService = inject(FiltersService);

  @ViewChildren("filterButton") filterButtons!: QueryList<
    ElementRef<HTMLButtonElement>
  >;
  @ViewChildren("categoryButton") categoryButtons!: QueryList<
    ElementRef<HTMLButtonElement>
  >;
  @ViewChild("detailsElement") detailsElement?: ElementRef<HTMLDetailsElement>;
  @ViewChild("categoriesContainer")
  categoriesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild("selectorsContainer")
  selectorsContainer?: ElementRef<HTMLDivElement>;
  @ViewChild("filtersContainer") filtersContainer?: ElementRef<HTMLDivElement>;
  @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;

  filterType = input.required<FilterSetType>();
  name = input.required<string>();
  filter = input.required<FilterSet>();
  disabled = input<boolean>(false);
  activateSearchBar = input<boolean>(false);
  showSearchBar = signal<boolean>(false);
  searchBarPlaceholder = input<string>("");
  activateSearchBarText = input<string>("");
  disableSearchBarText = input<string>("");
  filtersSelected = signal<FilterInterface[]>([]);
  filtersSelectedInput = input<FilterSet>();
  searchBarValue = signal<string>("");
  searchBarInput = input<string>("");
  isDetailsOpen = signal<boolean>(false);
  categoriesSelected = signal<FilterInterface[]>([]);
  filtersSelectedByType = computed<FilterSet>(() => ({
    spId: this.filter().spId,
    filters: this.filtersSelected(),
    filterSetType: this.filterType(),
    localName: this.name(),
    filtersCategories: this.categoriesSelected(),
  }));

  isEmpty = output<boolean>();
  updateFiltersSelected = output<FilterSet>();
  updateSearchValue = output<string>();

  ngOnInit(): void {
    const inputFilters = this.filtersSelectedInput();
    if (inputFilters?.filters && inputFilters.filters.length > 0) {
      this.filtersSelected.set(inputFilters.filters);

      if (
        inputFilters.filtersCategories &&
        inputFilters.filtersCategories.length > 0
      ) {
        this.categoriesSelected.set(inputFilters.filtersCategories);
      }
    }

    const inputSearchBarValue = this.searchBarInput();
    if (inputSearchBarValue && inputSearchBarValue.length > 0) {
      this.searchBarValue.set(inputSearchBarValue);
      this.showSearchBar.set(true);
    }
  }

  ngAfterViewInit(): void {
    if (
      this.filtersSelected().length > 0 ||
      (this.searchBarInput() && this.searchBarInput().length > 0)
    ) {
      setTimeout(() => {
        this.updateButtonStates();
      });
    }
  }

  constructor() {
    effect(() => {
      this.isEmpty.emit(
        this.filtersSelected().length === 0 &&
          this.searchBarValue().length === 0
      );
      this.updateFiltersSelected.emit(this.filtersSelectedByType());
    });

    effect(() => {
      if (this.disabled()) {
        this.filtersSelected.set([]);
        this.categoriesSelected.set([]);
        if (this.detailsElement) {
          this.detailsElement.nativeElement.open = false;
          this.isDetailsOpen.set(false);
        }

        this.filterButtons.forEach((buttonRef) => {
          buttonRef.nativeElement.removeAttribute("filterSelected");
        });

        this.categoryButtons.forEach((buttonRef) => {
          buttonRef.nativeElement.removeAttribute("categorySelected");
        });
      }
    });

    // Watch for changes in filtersSelectedInput and update button states
    effect(() => {
      const inputFilters = this.filtersSelectedInput();
      if (inputFilters?.filters && inputFilters.filters.length > 0) {
        this.filtersSelected.set(inputFilters.filters);

        if (
          inputFilters.filtersCategories &&
          inputFilters.filtersCategories.length > 0
        ) {
          this.categoriesSelected.set(inputFilters.filtersCategories);
        }

        // Update button states after a short delay to ensure view is updated
        setTimeout(() => {
          this.updateButtonStates();
        }, 0);
      }
    });
  }

  private activateParentCategory(selectedFilter: FilterInterface): void {
    if (!selectedFilter.parentCode) {
      return;
    }

    const parentCategory = this.categoriesSelected().find(
      (category) => category.spId === selectedFilter.parentCode
    );

    if (!parentCategory) {
      return;
    }

    this.categoryButtons.forEach((buttonRef) => {
      const buttonSpId = buttonRef.nativeElement.dataset.spId;
      if (buttonSpId === parentCategory.spId) {
        buttonRef.nativeElement.setAttribute("categorySelected", "true");
      }
    });

    if (parentCategory.parentCode) {
      this.filterButtons.forEach((buttonRef) => {
        const buttonSpId = buttonRef.nativeElement.dataset.spId;

        if (buttonSpId === parentCategory.spId) {
          buttonRef.nativeElement.setAttribute("categorySelected", "true");
        }
      });
    }

    this.activateParentCategory(parentCategory);
  }

  private updateButtonStates(): void {
    if (this.searchBarInput() && this.searchBarInput().length > 0) {
      this.searchBarValue.set(this.searchBarInput());
      this.showSearchBar.set(true);
      this.searchInput.nativeElement.value = this.searchBarInput();
      this.updateSearchValue.emit(this.searchBarInput());
    } else {
      // Match filters by spId instead of translation
      this.filterButtons.forEach((buttonRef) => {
        const buttonSpId = buttonRef.nativeElement.dataset.spId;

        const isSelected = this.filtersSelected().some(
          (f) => f.spId === buttonSpId
        );
        if (isSelected) {
          buttonRef.nativeElement.setAttribute("filterSelected", "true");
        } else {
          buttonRef.nativeElement.removeAttribute("filterSelected");
        }
      });

      this.filtersSelected().forEach((selectedFilter) => {
        this.activateParentCategory(selectedFilter);
        this.categoriesSelected().some((category) => {
          if (category.spId === selectedFilter.spId) {
            this.categoryButtons.forEach((buttonRef) => {
              const buttonSpId = buttonRef.nativeElement.dataset.spId;
              if (buttonSpId === category.spId) {
                buttonRef.nativeElement.setAttribute(
                  "categorySelected",
                  "true"
                );
              }
            });

            const foundCategory = this.filtersService.findInHierarchy(
              this.filter(),
              selectedFilter.spId
            );
            selectedFilter.translation = foundCategory.translation;

            category.filters.forEach((categorySubFilter) => {
              const foundSubFilter = this.filtersService.findInHierarchy(
                this.filter(),
                categorySubFilter.spId
              );
              categorySubFilter.translation = foundSubFilter.translation;
            });
          }
        });
      });
    }
  }

  onToggle(event: Event): void {
    const target = event.target as HTMLDetailsElement;
    if (this.disabled() && target.open) {
      target.open = false;
      return;
    }
    this.isDetailsOpen.set(target.open);
  }

  countSelectedFilters(): number {
    if (!this.showSearchBar()) {
      return this.filtersSelected().length;
    } else {
      const searchBarValue = this.searchBarValue().trim();
      if (searchBarValue.length === 0) {
        return 0;
      } else {
        return 1;
      }
    }
  }

  toggleSearchBar(): void {
    this.showSearchBar.update((current) => !current);
    if (!this.showSearchBar()) {
      this.searchBarValue.set("");
      this.updateSearchValue.emit(this.searchBarValue());
      this.isEmpty.emit(
        this.filtersSelected().length === 0 &&
          this.searchBarValue().length === 0
      );
    } else {
      this.deselectAll();
      this.categoriesSelected.set([]);
    }
  }

  clickFilter(
    filter: FilterInterface,
    buttonElement?: HTMLButtonElement
  ): void {
    if (filter.filters !== null && filter.filters.length > 0) {
      this.selectCategory(filter, buttonElement);
      this.piano.sendEvent("filter.selection", {
        filter_name: filter.translation,
        filter_type: this.filterType(),
      });
      return;
    }

    const isSelected = this.filtersSelected()
      .map((f) => f)
      .includes(filter);

    if (isSelected) {
      this.removeFilter(filter, buttonElement);
    } else {
      this.addFilter(filter, buttonElement);
      this.piano.sendEvent("filter.selection", {
        filter_name: filter.translation,
        filter_type: this.filterType(),
      });
    }
  }

  addFilter(filter: FilterInterface, buttonElement?: HTMLButtonElement): void {
    // Find if filter belongs to a selected parent category
    const parentCategory = this.categoriesSelected().find(
      (category) => category.spId === filter.parentCode
    );

    // If the filter has a parent category that's currently selected as a filter,
    // remove the parent from filters (child selection replaces parent selection)
    if (parentCategory) {
      this.filtersSelected.update((current) =>
        current.filter((f) => f.spId !== parentCategory.spId)
      );
    }

    // Add the filter to selected filters (avoid duplicates)
    if (!this.filtersSelected().some((f) => f.spId === filter.spId)) {
      this.filtersSelected.update((current) => [...current, filter]);
    }

    if (buttonElement) {
      buttonElement.setAttribute("filterSelected", "true");
    }
  }

  removeFilter(
    filter: FilterInterface,
    buttonElement?: HTMLButtonElement
  ): void {
    this.filtersSelected.update((current) =>
      current.filter((f) => f.spId !== filter.spId)
    );

    const parentCategory = this.categoriesSelected().find(
      (category) => category.spId === filter.parentCode
    );

    if (parentCategory) {
      // Check if any other children (at any depth) of the parent category are still selected
      const hasOtherChildrenSelected = this.hasAnyChildrenSelected(
        parentCategory,
        filter.spId
      );

      // Only add parent category back if NO children at any level are selected
      if (!hasOtherChildrenSelected) {
        this.addFilter(parentCategory);
      }
    }

    if (buttonElement) {
      buttonElement.removeAttribute("filterSelected");
    } else {
      // If no buttonElement is provided, find the button by spId
      this.filterButtons.forEach((buttonRef) => {
        const buttonSpId = buttonRef.nativeElement.dataset.spId;

        if (buttonSpId === filter.spId) {
          buttonRef.nativeElement.removeAttribute("filterSelected");
        }
      });
    }
  }

  private hasAnyChildrenSelected(
    category: FilterInterface,
    excludeSpId: string,
    depth: number = 0
  ): boolean {
    // Max depth protection
    if (depth >= 10) {
      return false;
    }

    if (!category.filters || category.filters.length === 0) {
      return false;
    }

    for (const child of category.filters) {
      // Skip the filter being removed
      if (child.spId === excludeSpId) {
        continue;
      }

      // Check if this child is selected
      if (this.filtersSelected().some((f) => f.spId === child.spId)) {
        return true;
      }

      // Check recursively if this child has filters
      if (child.filters && child.filters.length > 0) {
        if (this.hasAnyChildrenSelected(child, excludeSpId, depth + 1)) {
          return true;
        }
      }
    }

    return false;
  }

  selectAll(): void {
    let allFilters: FilterInterface[] = [];

    if (this.categoriesSelected().length > 0) {
      allFilters = this.categoriesSelected().flatMap(
        (category) => category.filters
      );
    } else if (this.filter().filters) {
      allFilters = [...this.filter().filters!];
    }

    this.filtersSelected.set(allFilters);
    this.filterButtons.forEach((buttonRef) => {
      buttonRef.nativeElement.setAttribute("filterSelected", "true");
    });
  }

  deselectAll(): void {
    this.filtersSelected.set([]);
    this.filterButtons.forEach((buttonRef) => {
      buttonRef.nativeElement.removeAttribute("filterSelected");
    });
    // Unselect all categories as well
    this.categoriesSelected.set([]);
    this.categoryButtons.forEach((buttonRef) => {
      buttonRef.nativeElement.removeAttribute("categorySelected");
    });
  }

  selectCategory(
    category: FilterInterface,
    buttonElement?: HTMLButtonElement
  ): void {
    const isAlreadySelected = this.categoriesSelected().some(
      (c) => c.spId === category.spId
    );

    if (isAlreadySelected) {
      this.removeCategory(category, buttonElement);
      this.removeFilter(category);
    } else {
      this.addCategory(category, buttonElement);
      this.addFilter(category);
    }
  }

  private addCategory(
    category: FilterInterface,
    buttonElement?: HTMLButtonElement
  ): void {
    // Only add if not already in the list
    if (!this.categoriesSelected().some((c) => c.spId === category.spId)) {
      this.categoriesSelected.update((current) => [...current, category]);
    }
    if (buttonElement) {
      buttonElement.setAttribute("categorySelected", "true");
    }
  }

  private removeChildrenFilters(
    parentFilter: FilterInterface,
    depth: number = 0
  ): void {
    // Max depth protection
    if (depth >= 10) {
      return;
    }

    if (!parentFilter.filters || parentFilter.filters.length === 0) {
      return;
    }

    parentFilter.filters.forEach((filter) => {
      // Remove from selected filters using spId comparison (not object reference)
      this.filtersSelected.update((current) =>
        current.filter((f) => f.spId !== filter.spId)
      );

      // Remove filter attribute from button by spId
      const button = this.filterButtons.find(
        (buttonRef) => buttonRef.nativeElement.dataset.spId === filter.spId
      );
      button?.nativeElement.removeAttribute("filterSelected");

      // Remove from selected categories using spId comparison
      this.categoriesSelected.update((current) =>
        current.filter((c) => c.spId !== filter.spId)
      );

      // Recursively remove children if this filter also has children
      if (filter.filters && filter.filters.length > 0) {
        this.removeChildrenFilters(filter, depth + 1);
      }
    });
  }

  private removeCategory(
    category: FilterInterface,
    buttonElement?: HTMLButtonElement
  ): void {
    this.removeChildrenFilters(category);
    this.categoriesSelected.update((current) =>
      current.filter((c) => c.spId !== category.spId)
    );
    if (buttonElement) {
      buttonElement.removeAttribute("categorySelected");
    }
  }
}
